import { mapActions, mapGetters, mapMutations, mapState } from "vuex"

import formatCurrency from '@/utils/formatCurrency'

const AUTO_RELOAD_INTERVAL = 2 * 60 * 1000 // 2 mins interval

const SEARCH_DEBOUNCE_MS = 400

// The API's cap on `filter.search`
const SEARCH_MAX_LENGTH = 100

// Every filter field of both roles; each page's API client sends only the ones its role reads
function emptyFilter() {
  return {
    channels: [],
    trafficSourceIds: [],
    campaignIds: [],
    offerIds: [],
    isSaleReported: null,
    search: ''
  }
}

/**
 * Shared by the buyer and seller Call Logs pages. A page using it provides:
 * - a b-table with `ref="callLogsTable"` whose provider sends `filter: this.filter` on `/calls` and `/total`
 *   (and on `/export`);
 * - `fetchFilterOptions()`, resolving to the dropdown options keyed by the filter field they fill,
 *   e.g. `{ campaignIds: [{ id, name }] }`. Other keys carry data that loads with the options (the buyer's
 *   Performance card rows) and are not filters.
 *
 * Filter state lives on the page only, so leaving the page resets it. Date range and time zone stay in the store.
 */
export default {
  data() {
    return {
      calls: [],
      pageSize: 25,
      isLoading: false,
      totalItems: 0,
      currentPage: 1,
      isExportLoading: false,
      // The applied filter, what the requests send
      filter: emptyFilter(),
      // What the search box shows; becomes `filter.search` after the debounce, on Enter or on clear
      searchText: '',
      searchMaxLength: SEARCH_MAX_LENGTH,
      // Keyed by filter field: { campaignIds: [{ id, name }], ... }
      filterOptions: {}
    }
  },

  computed: {
    ...mapGetters('auth', [
      'accountId'
    ]),

    ...mapState('callLogs', [
      'dateRange',
      'timeZone',
      'autoRefresh'
    ])
  },

  watch: {
    // Reloads the calls with the current filter, and the options so the buyer's traffic source breakdown stays live
    autoRefresh (newValue) {
      if (newValue) {
        this.autoReloadTimer = setInterval(async () => {
          await Promise.all([
            this.reloadData(),
            this.loadFilterOptions()
          ])
        }, AUTO_RELOAD_INTERVAL)
      } else {
        this.clearAutoReloadInterval()
      }
    },
  },

  created () {
    this.autoReloadTimer = null
    this.searchTimer = null
    this.totalsReloadPending = true
    // Only the latest options request may set the options: an older window's answer can land after it
    this.filterOptionsRequestId = 0

    this.getCallLogsTimeZone()
  },

  mounted () {
    this.loadFilterOptions()
  },

  destroyed () {
    this.clearAutoReloadInterval()
    this.cancelSearchTimer()
  },

  methods: {
    ...mapActions('callLogs', ['setCallLogsTimeZone', 'getCallLogsTimeZone']),

    ...mapMutations('callLogs', ['TOGGLE_AUTO_REFRESH', 'SET_DATE_RANGE']),

    formatCurrency(value) {
      return formatCurrency(value, undefined, undefined, true)
    },

    async reloadData() {
      this.$refs.callLogsTable.refresh()
    },

    resetTable() {
      this.currentPage = 1
      this.totalItems = 0
      this.totalsReloadPending = true
    },

    /**
     * Asked by the table provider before each load: true when the totals must be fetched with it. A flag, not
     * just `totalItems === 0`: a load still in flight when the filter changes sets totalItems again after the
     * reset, and the load queued behind it would then keep the previous filter's totals.
     */
    takeTotalsReload() {
      const reload = this.totalsReloadPending || this.totalItems === 0

      this.totalsReloadPending = false

      return reload
    },

    clearAutoReloadInterval() {
      if (this.autoReloadTimer) {
        clearInterval(this.autoReloadTimer)
        this.autoReloadTimer = null
      }
    },

    async handleTimeZoneUpdate(value) {
      this.setCallLogsTimeZone(value)

      this.resetTable()

      // In parallel with the table; a selection the new options lack reloads it once more
      this.loadFilterOptions()

      await this.reloadData()
    },

    async handleDateRangeUpdate([from, to]) {
      this.SET_DATE_RANGE([from, to])
      this.resetTable()

      this.loadFilterOptions()

      await this.reloadData()
    },

    // Table and totals for the new filter, from page 1
    async handleFilterChange() {
      this.resetTable()

      await this.reloadData()
    },

    setFilter(field, value) {
      this.filter[field] = value

      return this.handleFilterChange()
    },

    onSearchInput(value) {
      this.searchText = value

      this.cancelSearchTimer()
      this.searchTimer = setTimeout(() => this.applySearch(), SEARCH_DEBOUNCE_MS)
    },

    // Enter, the clear "x" and the debounce all land here
    applySearch() {
      this.cancelSearchTimer()

      const search = this.searchText.trim()

      if (search === this.filter.search) {
        return
      }

      this.filter.search = search

      return this.handleFilterChange()
    },

    clearSearch() {
      this.searchText = ''

      return this.applySearch()
    },

    cancelSearchTimer() {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    },

    async loadFilterOptions() {
      const requestId = ++this.filterOptionsRequestId

      try {
        const options = await this.fetchFilterOptions()

        if (requestId !== this.filterOptionsRequestId) {
          return
        }

        this.filterOptions = options

        this.dropStaleSelections()
      } catch (error) {
        // The table works without the dropdowns; they keep the options they had
        console.error('Error loading call logs filter options:', error)
      }
    },

    // A value the new window has no calls for can't stay selected: the table would show nothing for it
    dropStaleSelections() {
      let changed = false

      Object.entries(this.filterOptions).forEach(([field, options]) => {
        if (!Array.isArray(this.filter[field])) {
          return
        }

        const available = new Set(options.map(option => option.id))
        const kept = this.filter[field].filter(id => available.has(id))

        if (kept.length !== this.filter[field].length) {
          this.filter[field] = kept
          changed = true
        }
      })

      if (changed) {
        this.handleFilterChange()
      }
    }
  },
}
